import { useEffect, useRef } from "react";
import { useAppDispatch } from "./useAppDispatch";
import { useAppSelector } from "./useAppSelector";
import { fetchPlansData } from "@/app/store/plans/actions";
import {
  canAccessFeature,
  getPlanPermissions,
  getPlanLimits,
  isPlanUpgrade,
  isPlanDowngrade,
} from "@/app/store/plans/types";
import { Plan } from "@/api/plans";
import { PLAN_FEATURES } from "@/app/store/plans/constants";

export const usePlans = () => {
  const dispatch = useAppDispatch();
  const {
    currentPlan,
    availablePlans,
    permissions,
    isLoading,
    error,
    hasAttemptedFetch,
  } = useAppSelector((state) => state.plans);
  const fetchInProgressRef = useRef(false);

  useEffect(() => {
    // не загружать, если:
    // 1. уже есть данные
    // 2. уже идет загрузка
    // 3. есть ошибка и уже была попытка загрузки (бесконечный цикл)
    // 4. запрос уже в процессе (параллельные запросы)
    const shouldFetch =
      availablePlans.length === 0 &&
      !isLoading &&
      !(error && hasAttemptedFetch) &&
      !fetchInProgressRef.current;

    if (shouldFetch) {
      fetchInProgressRef.current = true;
      dispatch(fetchPlansData()).finally(() => {
        fetchInProgressRef.current = false;
      });
    }
  }, [dispatch, availablePlans.length, isLoading, error, hasAttemptedFetch]);

  return {
    currentPlan,
    availablePlans,
    permissions,
    isLoading,
    error,

    canAccessFeature: (featureBit: number) =>
      canAccessFeature(currentPlan, featureBit),
    getPlanPermissions: (plan: Plan) => getPlanPermissions(plan, permissions),
    getPlanLimits: (plan: Plan) => getPlanLimits(plan),
    isPlanUpgrade: (targetPlan: Plan) => isPlanUpgrade(currentPlan, targetPlan),
    isPlanDowngrade: (targetPlan: Plan) =>
      isPlanDowngrade(currentPlan, targetPlan),

    refreshPlans: () => dispatch(fetchPlansData()),

    // Удобные методы для проверки функций плана
    canAccessBasePermission: () =>
      canAccessFeature(currentPlan, PLAN_FEATURES.BASE_PERMISSION),
    canAccessBillings: () =>
      canAccessFeature(currentPlan, PLAN_FEATURES.BILLINGS),
    canAccessMeetingRooms: () =>
      canAccessFeature(currentPlan, PLAN_FEATURES.MEETING_ROOMS),
    canAccessTickets: () =>
      canAccessFeature(currentPlan, PLAN_FEATURES.TICKETS),
    canAccessPrinters: () =>
      canAccessFeature(currentPlan, PLAN_FEATURES.PRINTERS),
    canAccessLocks: () => canAccessFeature(currentPlan, PLAN_FEATURES.LOCKS),
    canAccessAI: () => canAccessFeature(currentPlan, PLAN_FEATURES.AI),
    canAccessTG: () => canAccessFeature(currentPlan, PLAN_FEATURES.TG),
  };
};
